import { SelectHTMLAttributes, forwardRef } from 'react';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface FormSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  error?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
}

/**
 * Styled select dropdown component
 */
export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  ({ options, error, fullWidth = true, placeholder, className = '', ...props }, ref) => {
    const baseClasses = 'rounded-md border px-3 py-2 text-sm transition-colors';
    const widthClasses = fullWidth ? 'w-full' : '';
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-zinc-300 focus:border-blue-500 focus:ring-blue-500';
    const interactionClasses = 'focus:outline-none focus:ring-2 focus:ring-opacity-50';
    const disabledClasses = props.disabled ? 'bg-zinc-100 cursor-not-allowed' : 'bg-white';

    return (
      <select
        data-tsx-id="form-select"
        ref={ref}
        className={`${baseClasses} ${widthClasses} ${stateClasses} ${interactionClasses} ${disabledClasses} ${className}`}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
    );
  }
);

FormSelect.displayName = 'FormSelect';
